/**
 * Minimal model for expired store presentation.
 * Handles nested or flat API shapes defensively.
 */
export default class ExpiredStoreModel {
  private _data: any;

  constructor(data: any) {
    this._data = data || {};
  }

  static from(data: any) {
    return new ExpiredStoreModel(data);
  }

  get id(): string | undefined {
    return this._data?.store?.id ?? this._data?.id;
  }

  get name(): string {
    return this._data?.store?.name ?? this._data?.name ?? '';
  }

  get logoImageUrl(): string | undefined {
    return this._data?.store?.logo_image_url ?? this._data?.logo_image_url;
  }

  get addressLine1(): string {
    return this._data?.store?.address_line1 ?? this._data?.address_line1 ?? '';
  }

  get city(): string {
    return this._data?.store?.city ?? this._data?.city ?? '';
  }

  get state(): string {
    return this._data?.store?.state ?? this._data?.state ?? '';
  }

  get postalCode(): string {
    return this._data?.store?.postal_code ?? this._data?.postal_code ?? '';
  }

  get renewalDate(): string | undefined {
    return this._data?.store?.renewal_date ?? this._data?.renewal_date;
  }

  get recurringPriceId(): string | undefined {
    return this._data?.store?.recurring_price_id ?? this._data?.recurring_price_id;
  }

  get formattedAddress(): string {
    const parts = [this.addressLine1, this.city].filter(Boolean);
  
    const statePostal = [this.state, this.postalCode].filter(Boolean).join(' ');
  
    if (statePostal) {
      parts.push(statePostal);
    }
  
    return parts.join(', ');
  }

  get formattedRenewalDate(): string {
    if (!this.renewalDate) return '';
    const date = new Date(this.renewalDate);
    return isNaN(date.getTime())
      ? ''
      : date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); // DD/MM/YYYY
  }

  get expiredAtTimestamp(): number {
    if (!this.renewalDate) return Number.NEGATIVE_INFINITY;
    const date = new Date(this.renewalDate);
    return isNaN(date.getTime()) ? Number.NEGATIVE_INFINITY : date.getTime();
  }

  static sortByExpiration(models: ExpiredStoreModel[]) {
    return [...models].sort((a, b) => b.expiredAtTimestamp - a.expiredAtTimestamp);
  }
}


export const mapExpiredStoresToModels = (stores: any[] | undefined | null) =>
  (stores || []).map((s) => ExpiredStoreModel.from(s));

export const getExpiredStoresCount = (stores: any[] | undefined | null) =>
  mapExpiredStoresToModels(stores).length;

